<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { string, object, boolean } from 'yup';

const {
    tenant,
    googleAdsPlanningKeyword,
    googleAdsPlanningKeywordCountries,
    googleAdsPlanningKeywordLanguages,
    googleAdsPlanningKeywordPlanNetworks,
} = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    googleAdsPlanningKeyword: {
        required: false,
        type: Object,
    },
    googleAdsPlanningKeywordCountries: {
        required: true,
        type: Array,
    },
    googleAdsPlanningKeywordLanguages: {
        required: true,
        type: Array,
    },
    googleAdsPlanningKeywordPlanNetworks: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    keyword: googleAdsPlanningKeyword ? googleAdsPlanningKeyword.keyword : '',
    country:
        googleAdsPlanningKeyword && googleAdsPlanningKeyword.country
            ? googleAdsPlanningKeywordCountries.find(
                  (country) =>
                      country.value === googleAdsPlanningKeyword.country.value,
              )
            : null,
    language:
        googleAdsPlanningKeyword && googleAdsPlanningKeyword.language
            ? googleAdsPlanningKeywordLanguages.find(
                  (language) =>
                      language.value ===
                      googleAdsPlanningKeyword.language.value,
              )
            : null,
    planNetwork: googleAdsPlanningKeyword
        ? googleAdsPlanningKeywordPlanNetworks.find(
              (network) =>
                  network.value === googleAdsPlanningKeyword.planNetwork.value,
          )
        : googleAdsPlanningKeywordPlanNetworks[0],
    includeAdult: googleAdsPlanningKeyword
        ? googleAdsPlanningKeyword.includeAdult
        : false,
});

const isSubmitting = ref(false);

const validationSchema = object({
    keyword: string().required('Ce champ est obligatoire.'),
    country: object().nullable(),
    language: object().nullable(),
    planNetwork: object().required('Ce champ est obligatoire.'),
    includeAdult: boolean().required('Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sea.postPlanningKeyword', {
                tenant: tenant.id,
                ...(googleAdsPlanningKeyword
                    ? {
                          googleAdsPlanningKeywordId:
                              googleAdsPlanningKeyword.id,
                      }
                    : {}),
            }),
            {
                keyword: form.keyword,
                country: form.country ? form.country.value : null,
                language: form.language ? form.language.value : null,
                planNetwork: form.planNetwork.value,
                includeAdult: form.includeAdult,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.sea.planningKeywords', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid>
                    <InputField
                        v-model="form.keyword"
                        label="Mot-clé"
                        field-name="keyword"
                        field-id="keyword"
                        type="keyword"
                        required
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <ListboxField
                        v-model="form.country"
                        field-id="country"
                        field-name="country"
                        label="Pays"
                        :options="googleAdsPlanningKeywordCountries"
                        has-empty-option
                    >
                        <template #buttonContent>
                            {{ form.country.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-model="form.language"
                        field-id="language"
                        field-name="language"
                        label="Langue"
                        :options="googleAdsPlanningKeywordLanguages"
                        has-empty-option
                    >
                        <template #buttonContent>
                            {{ form.language.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid>
                    <ListboxField
                        v-model="form.planNetwork"
                        field-id="planNetwork"
                        field-name="planNetwork"
                        label="Réseau"
                        :options="googleAdsPlanningKeywordPlanNetworks"
                        required
                    >
                        <template #buttonContent>
                            {{ form.planNetwork.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="1">
                    <SwitchField
                        v-model="form.includeAdult"
                        field-name="includeAdult"
                        field-id="includeAdult"
                        label="Inclure les contenus pour adultes"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="googleAdsPlanningKeyword"
                    :url="
                        route(
                            'front.projectManagement.sea.deletePlanningKeyword',
                            {
                                tenant: tenant.id,
                                googleAdsPlanningKeyword:
                                    googleAdsPlanningKeyword.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.sea.planningKeywords',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
